import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WatchlistService } from "../services/watchlist-service";
import type { Asset, Watchlist } from "@/schemas";
import { toast } from "sonner";
import { Check, Loader2, Plus, Bookmark, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { AssetSearch } from "@/components/ui/asset-search";
import { AssetLogo } from "@/components/asset/asset-logo";

interface AddAssetToWatchlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  watchlists: Watchlist[];
  onItemAdded: () => void;
  onWatchlistCreated: (watchlist: Watchlist) => void;
}

export function AddAssetToWatchlistModal({
  open,
  onOpenChange,
  watchlists,
  onItemAdded,
  onWatchlistCreated,
}: AddAssetToWatchlistModalProps) {
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [targetPrice, setTargetPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [creatingList, setCreatingList] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedAsset(null);
      setSelectedIds(new Set());
      setNote("");
      setTargetPrice("");
      setNewListName("");
    }
  }, [open]);

  const handleToggle = (watchlistId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(watchlistId)) {
        next.delete(watchlistId);
      } else {
        next.add(watchlistId);
      }
      return next;
    });
  };

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (!name) return;

    try {
      setCreatingList(true);
      const created = await WatchlistService.create(name);
      onWatchlistCreated(created);
      setSelectedIds((prev) => new Set(prev).add(created.watchlist_id));
      setNewListName("");
      toast.success("Watchlist created");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create watchlist");
    } finally {
      setCreatingList(false);
    }
  };

  const handleSave = async () => {
    if (!selectedAsset || selectedIds.size === 0) return;

    try {
      setSaving(true);
      const payload: {
        asset_id: string;
        note?: string;
        target_price?: number;
      } = {
        asset_id: selectedAsset.asset_id,
      };
      if (note.trim()) payload.note = note.trim();
      const price = targetPrice.trim() ? parseFloat(targetPrice) : undefined;
      if (price && !isNaN(price)) payload.target_price = price;

      const errors: string[] = [];
      let addedCount = 0;

      for (const watchlistId of selectedIds) {
        try {
          await WatchlistService.addItem(watchlistId, payload);
          addedCount++;
        } catch (error) {
          console.error(error);
          errors.push(
            error instanceof Error ? error.message : "Failed to add item"
          );
        }
      }

      if (addedCount > 0) {
        toast.success(
          `${selectedAsset.symbol} added to ${addedCount} watchlist${addedCount > 1 ? "s" : ""}`
        );
      }
      if (errors.length > 0) {
        toast.error(errors[0]);
      }

      onItemAdded();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to add asset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Add Asset to Watchlist
          </DialogTitle>
          <DialogDescription>
            Search for an asset and choose one or more watchlists
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Asset Search */}
          <div className="space-y-2">
            <Label>Asset</Label>
            {selectedAsset ? (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border">
                <AssetLogo asset={selectedAsset} size="sm" />
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-sm">
                    {selectedAsset.symbol}
                  </span>
                  <span className="text-xs text-muted-foreground ml-2 truncate">
                    {selectedAsset.name}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => setSelectedAsset(null)}
                >
                  Change
                </Button>
              </div>
            ) : (
              <AssetSearch
                onSelect={(asset) => setSelectedAsset(asset)}
                placeholder="Search for a stock, ETF, fund, or crypto..."
                assetType="stock,etf,fund,crypto"
              />
            )}
          </div>

          {selectedAsset && (
            <>
              {/* Watchlist Selection */}
              <div className="space-y-2">
                <Label>Select Watchlists</Label>
                {watchlists.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No watchlists yet. Create one below.
                  </p>
                ) : (
                  <ScrollArea className="h-40 rounded-md border">
                    <div className="p-2 space-y-1">
                      {watchlists.map((wl) => {
                        const isSelected = selectedIds.has(wl.watchlist_id);
                        return (
                          <button
                            key={wl.watchlist_id}
                            type="button"
                            onClick={() => handleToggle(wl.watchlist_id)}
                            className={cn(
                              "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                              isSelected
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-muted"
                            )}
                          >
                            <div
                              className={cn(
                                "flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
                                isSelected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-muted-foreground/30"
                              )}
                            >
                              {isSelected && <Check className="h-3.5 w-3.5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {wl.name}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </div>

              {/* Create New List */}
              <div className="flex gap-2">
                <Input
                  placeholder="New watchlist name..."
                  className="h-9 text-sm"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateList();
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={handleCreateList}
                  disabled={creatingList || !newListName.trim()}
                >
                  {creatingList ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Note + Target Price */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Note (optional)</Label>
                  <Input
                    className="h-9 text-sm"
                    placeholder="e.g. Buy on dip"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Target Price (optional)</Label>
                  <Input
                    type="number"
                    className="h-9 text-sm"
                    placeholder="e.g. 150.00"
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(e.target.value)}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || selectedIds.size === 0}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Bookmark className="h-4 w-4 mr-2" />
                  )}
                  Add to {selectedIds.size || ""} Watchlist
                  {selectedIds.size > 1 ? "s" : ""}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
